<agr-form-dialog
  [formGroup]="formGroup"
  [data]="data"
  (confirm)="confirm()"
  (cancel)="cancel()"
  data-test="create-nutrient-property-dialog"
>
  <div class="flex--container">
    <!-- Source select box -->
    <div class="flex--row flex--gutter">
      <mat-form-field class="flex__child--auto min-width-350" appearance="outline">
        <mat-label>{{ 'nutrientProperties.source' | translate }}</mat-label>
        <mat-select formControlName="source" data-test="create-nutrient-property-source">
          @for (source of data.nutrientSources; track source) {
            <mat-option [value]="source.source">
              {{ source.source }}
            </mat-option>
          }
        </mat-select>
        @if (formGroup.controls.source.hasError('required')) {
          <mat-error> {{ 'nutrientProperties.source' | translate }} is required </mat-error>
        }
        @if (formGroup.controls.source.hasError('serverError')) {
          <mat-error>
            {{ formGroup.controls.source.getError('serverError') }}
          </mat-error>
        }
      </mat-form-field>
    </div>

    <!-- NutrientDesignation select box -->
    <div class="flex--row flex--gutter">
      <mat-form-field class="flex__child--auto min-width-350" appearance="outline">
        <mat-label>{{ 'nutrientProperties.nutrientDesignation' | translate }}</mat-label>
        <mat-select
          formControlName="nutrientDesignation"
          data-test="create-nutrient-property-nutrient-designation"
        >
          @for (designation of data.nutrientDesignations; track designation) {
            <mat-option [value]="designation.designation">
              {{ designation.designation }}
            </mat-option>
          }
        </mat-select>
        @if (formGroup.controls.nutrientDesignation.hasError('required')) {
          <mat-error>
            {{ 'nutrientProperties.nutrientDesignation' | translate }} is required
          </mat-error>
        }
        @if (formGroup.controls.nutrientDesignation.hasError('serverError')) {
          <mat-error>
            {{ formGroup.controls.nutrientDesignation.getError('serverError') }}
          </mat-error>
        }
      </mat-form-field>
    </div>

    <!-- SoilType select box -->
    <div class="flex--row flex--gutter">
      <mat-form-field class="flex__child--auto min-width-350" appearance="outline">
        <mat-label>{{ 'nutrientProperties.soilType' | translate }}</mat-label>
        <mat-select formControlName="soilTypeId" data-test="create-nutrient-property-soil-type">
          @for (soilType of data.soilTypes; track soilType) {
            <mat-option [value]="soilType.id">
              {{ soilType.name }}
            </mat-option>
          }
        </mat-select>
        @if (formGroup.controls.soilTypeId.hasError('required')) {
          <mat-error> {{ 'nutrientProperties.soilType' | translate }} is required </mat-error>
        }
        @if (formGroup.controls.soilTypeId.hasError('serverError')) {
          <mat-error>
            {{ formGroup.controls.soilTypeId.getError('serverError') }}
          </mat-error>
        }
      </mat-form-field>
    </div>

    <!-- SampleNumber select box -->
    <div class="flex--row flex--gutter">
      <mat-form-field class="flex__child--auto min-width-350" appearance="outline">
        <mat-label>{{ 'nutrientProperties.sampleNumber.title' | translate }}</mat-label>
        <mat-select
          formControlName="sampleNumber"
          data-test="create-nutrient-property-sample-number"
        >
          @for (sampleNumber of getSampleNumbers(); track sampleNumber) {
            <mat-option [value]="sampleNumber">
              {{ `nutrientProperties.sampleNumber.${sampleNumber}` | translate }}
            </mat-option>
          }
        </mat-select>
        @if (formGroup.controls.sampleNumber.hasError('required')) {
          <mat-error>
            {{ 'nutrientProperties.sampleNumber.title' | translate }} is required
          </mat-error>
        }
        @if (formGroup.controls.sampleNumber.hasError('serverError')) {
          <mat-error>
            {{ formGroup.controls.sampleMoment.getError('serverError') }}
          </mat-error>
        }
      </mat-form-field>
    </div>

    <!-- SoilTargetUoM select box -->
    <div class="flex--row flex--gutter">
      <mat-form-field class="flex__child--auto min-width-350" appearance="outline">
        <mat-label>{{ 'nutrientProperties.soilTargetUoM' | translate }}</mat-label>
        <mat-select
          formControlName="soilTargetUoM"
          data-test="create-nutrient-property-soil-target-uom"
        >
          @for (unit of filteredUnitsOfMeasure; track unit) {
            <mat-option [value]="unit.name">
              {{ unit.name }}
            </mat-option>
          }
        </mat-select>
        @if (formGroup.controls.soilTargetUoM.hasError('serverError')) {
          <mat-error>
            {{ formGroup.controls.soilTargetUoM.getError('serverError') }}
          </mat-error>
        }
      </mat-form-field>
    </div>

    <!-- SoilTargetLow input -->
    <div class="flex--row flex--gutter">
      <mat-form-field class="flex__child--auto min-width-350" appearance="outline">
        <mat-label>{{ 'nutrientProperties.soilTargetLow' | translate }}</mat-label>
        <input
          matInput
          formControlName="soilTargetLow"
          agrNumber
          appDecimalSeparator
          step="0.01"
          data-test="create-nutrient-property-soil-target-low"
        />
        @if (
          formGroup.controls.soilTargetLow.hasError('min') ||
          formGroup.controls.soilTargetLow.hasError('max')
        ) {
          <mat-error>
            {{ 'nutrientProperties.soilTargetLow' | translate }} must be between 0 and 99999
          </mat-error>
        }
        @if (formGroup.controls.soilTargetLow.hasError('decimal')) {
          <mat-error>
            {{ formGroup.controls.soilTargetLow.getError('decimal') }}
          </mat-error>
        }
        @if (formGroup.controls.soilTargetLow.hasError('serverError')) {
          <mat-error>
            {{ formGroup.controls.soilTargetLow.getError('serverError') }}
          </mat-error>
        }
      </mat-form-field>
    </div>

    <!-- SoilTargetHigh input -->
    <div class="flex--row flex--gutter">
      <mat-form-field class="flex__child--auto min-width-350" appearance="outline">
        <mat-label>{{ 'nutrientProperties.soilTargetHigh' | translate }}</mat-label>
        <input
          matInput
          formControlName="soilTargetHigh"
          agrNumber
          appDecimalSeparator
          step="0.01"
          data-test="create-nutrient-property-soil-target-high"
        />
        @if (
          formGroup.controls.soilTargetHigh.hasError('min') ||
          formGroup.controls.soilTargetHigh.hasError('max')
        ) {
          <mat-error>
            {{ 'nutrientProperties.soilTargetHigh' | translate }} must be between 0 and 99999
          </mat-error>
        }
        @if (formGroup.controls.soilTargetHigh.hasError('decimal')) {
          <mat-error>
            {{ formGroup.controls.soilTargetHigh.getError('decimal') }}
          </mat-error>
        }
        @if (formGroup.controls.soilTargetHigh.hasError('serverError')) {
          <mat-error>
            {{ formGroup.controls.soilTargetHigh.getError('serverError') }}
          </mat-error>
        }
      </mat-form-field>
    </div>

    <!-- PlantTargetUoM select box -->
    <div class="flex--row flex--gutter">
      <mat-form-field class="flex__child--auto min-width-350" appearance="outline">
        <mat-label>{{ 'nutrientProperties.plantTargetUoM' | translate }}</mat-label>
        <mat-select
          formControlName="plantTargetUoM"
          data-test="create-nutrient-property-plant-target-uom"
        >
          @for (unit of filteredUnitsOfMeasure; track unit) {
            <mat-option [value]="unit.name">
              {{ unit.name }}
            </mat-option>
          }
        </mat-select>
        @if (formGroup.controls.plantTargetUoM.hasError('serverError')) {
          <mat-error>
            {{ formGroup.controls.plantTargetUoM.getError('serverError') }}
          </mat-error>
        }
      </mat-form-field>
    </div>

    <!-- PlantTargetLow input -->
    <div class="flex--row flex--gutter">
      <mat-form-field class="flex__child--auto min-width-350" appearance="outline">
        <mat-label>{{ 'nutrientProperties.plantTargetLow' | translate }}</mat-label>
        <input
          matInput
          formControlName="plantTargetLow"
          agrNumber
          appDecimalSeparator
          step="0.01"
          data-test="create-nutrient-property-plant-target-low"
        />
        @if (
          formGroup.controls.plantTargetLow.hasError('min') ||
          formGroup.controls.plantTargetLow.hasError('max')
        ) {
          <mat-error>
            {{ 'nutrientProperties.plantTargetLow' | translate }} must be between 0 and 99999
          </mat-error>
        }
        @if (formGroup.controls.plantTargetLow.hasError('decimal')) {
          <mat-error>
            {{ formGroup.controls.plantTargetLow.getError('decimal') }}
          </mat-error>
        }
        @if (formGroup.controls.plantTargetLow.hasError('serverError')) {
          <mat-error>
            {{ formGroup.controls.plantTargetLow.getError('serverError') }}
          </mat-error>
        }
      </mat-form-field>
    </div>

    <!-- PlantTargetHigh input -->
    <div class="flex--row flex--gutter">
      <mat-form-field class="flex__child--auto min-width-350" appearance="outline">
        <mat-label>{{ 'nutrientProperties.plantTargetHigh' | translate }}</mat-label>
        <input
          matInput
          formControlName="plantTargetHigh"
          agrNumber
          appDecimalSeparator
          step="0.01"
          data-test="create-nutrient-property-plant-target-high"
        />
        @if (
          formGroup.controls.plantTargetHigh.hasError('min') ||
          formGroup.controls.plantTargetHigh.hasError('max')
        ) {
          <mat-error>
            {{ 'nutrientProperties.plantTargetHigh' | translate }} must be between 0 and 99999
          </mat-error>
        }
        @if (formGroup.controls.plantTargetHigh.hasError('decimal')) {
          <mat-error>
            {{ formGroup.controls.plantTargetHigh.getError('decimal') }}
          </mat-error>
        }
        @if (formGroup.controls.plantTargetHigh.hasError('serverError')) {
          <mat-error>
            {{ formGroup.controls.plantTargetHigh.getError('serverError') }}
          </mat-error>
        }
      </mat-form-field>
    </div>
  </div>
</agr-form-dialog>
